import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";

import {
  login,
  setIsLoginAttemptBlocked,
  setIsLoginPending,
} from "../actions/auth";
import { ExternalLinks } from "../constants/index.js";

import LogoSrc from "../assets/images/logo.svg";
import { useNavigate } from "react-router-dom";
import { NotificationToaster } from "../utils/notificationToaster";
import { fetchUserFromEmailAction } from "../actions/auth";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../config/ssoConfig";

const StyledForm = styled("form")(({ theme }) => ({
  width: "100%",
}));
const StyledBtnSubmit = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 1),
  padding: theme.spacing(2.4, 0),
  fontSize: "14px",
}));
const StyledForgotLink = styled(Link)(({ theme }) => ({
  display: "inline-block",
  marginBottom: theme.spacing(2),
  cursor: "pointer",
  "&:hover": {
    color: "#fa03c1",
  },
}));
const StyledLogo = styled("img")(({ theme }) => ({
  marginBottom: theme.spacing(7.5),
  cursor: "pointer",
}));
const StyledErrorMessage = styled("div")(({ theme }) => ({
  fontSize: "16px",
  textAlign: "center",
  width: "100%",
  margin: "1em 0",
  color: "red",
  "&:hover": {
    color: "#fc7373",
  },
}));

const LoginForm = () => {
  const navigate = useNavigate();
  const loader = useSelector((state) => state.appLoader);

  // hooks
  const dispatch = useDispatch();

  // selectors
  const { hasLoginPending, errorMessage, loginAttemptsCount, isLoginBlocked } =
    useSelector((state) => state.auth);
  const { isDarkMode } = useSelector((state) => state.settings.generalSetting);

  // hooks
  const {
    register,
    handleSubmit: rhfHandleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });
  const { instance } = useMsal();

  // variables
  const inputVariant = isDarkMode ? "outlined" : "outlined";

  // handlers
  const handleFormSubmit = (data) => {
    const isLastAttempt = loginAttemptsCount >= 2;
    dispatch(login(data.username, data.password, isLastAttempt))
      .then((permissionsResponse) => {
        if (permissionsResponse.length > 0) {
          // if (permissionsResponse.some(permission => permission.code.toLowerCase().includes("dashboard"))) {
          //     navigate("/dashboard")
          // } else {
          //     navigate(`/${permissionsResponse[0].path}`)
          // }

          navigate("/dashboard");
          navigate(0);
        } else {
          NotificationToaster({
            message: "You Don't Have Permissions",
            type: "warning",
          });
        }
      })
      .catch((err) => {
        navigate("/login");
      });
  };

  const handleSSOLogin = () => {
    instance
      .loginPopup(loginRequest)
      .catch((e) => {
        console.log(e);
      })
      .then((response) => {
        localStorage.setItem(
          "token",
          JSON.stringify(`Bearer ${response.accessToken}`),
        );
        dispatch(
          fetchUserFromEmailAction(
            response.account.username,
            response.accessToken,
          ),
        ).then((res) => {
          navigate("/dashboard");
          navigate(0);
        });
      });
  };

  // effects
  useEffect(() => {
    if (isLoginBlocked) {
      setTimeout(() => {
        dispatch(setIsLoginAttemptBlocked(false));
        dispatch(setIsLoginPending(false));
      }, 3000);
    }
  }, [errorMessage, isLoginBlocked]);

  return (
    <Grid
      container
      direction={"row"}
      justifyContent={"center"}
      alignItems={"center"}
      height={"100vh"}
    >
      <Grid item xs={3} alignItems={"center"}>
        <Grid container alignContent="center" justifyContent={"center"}>
          <Link to={"/"}>
            <StyledLogo alt="brand-logo" src={LogoSrc} />
          </Link>

          <StyledForm noValidate onSubmit={rhfHandleSubmit(handleFormSubmit)}>
            <Grid container direction="row">
              <Grid item xs={12}>
                <TextField
                  {...register("username", {
                    required: "Username is required.",
                    minLength: {
                      value: 3,
                      message: "Username length at least 3 characters",
                    },
                  })}
                  disabled={hasLoginPending}
                  aria-disabled={hasLoginPending}
                  margin="normal"
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  variant={inputVariant}
                  placeholder="Enter Username"
                  // InputLabelProps={{ shrink: true }}
                  inputProps={{
                    sx: {
                      "&::placeholder": {
                        color: "inherit",
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "inherit",
                    },
                  }}
                  error={!!errors?.username || !!errorMessage}
                  helperText={`${errors?.username ? errors.username.message : ""}`}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register("password", {
                    required: "Password is required.",
                    minLength: {
                      value: 6,
                      message: "Password length at least 6 characters",
                    },
                  })}
                  disabled={hasLoginPending}
                  aria-disabled={hasLoginPending}
                  margin="normal"
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  variant={inputVariant}
                  placeholder="Enter Password"
                  // InputLabelProps={{ shrink: true }}
                  inputProps={{
                    sx: {
                      "&::placeholder": {
                        color: "inherit",
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "inherit",
                    },
                  }}
                  error={!!errors?.password || !!errorMessage}
                  helperText={`${errors?.password ? errors.password.message : ""}`}
                />
              </Grid>
              <StyledBtnSubmit
                aria-disabled={hasLoginPending}
                color="primary"
                disabled={hasLoginPending}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                {hasLoginPending ? "Loading..." : "Login"}
              </StyledBtnSubmit>
              <StyledBtnSubmit
                // aria-disabled={hasLoginPending}
                color="primary"
                // disabled={hasLoginPending}
                fullWidth
                size="large"
                type="button"
                variant="contained"
                onClick={handleSSOLogin}
              >
                SSO Login
              </StyledBtnSubmit>
              <Grid container direction={"column"} alignItems={"center"}>
                <Grid item>
                  <StyledForgotLink
                    href={ExternalLinks.forgotUsername}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Forgot Username?
                  </StyledForgotLink>
                </Grid>
                <Grid item>
                  <StyledForgotLink
                    href={ExternalLinks.forgotPassword}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Forgot Password?
                  </StyledForgotLink>
                </Grid>
                <Grid container alignItems={"center"}>
                  {isLoginBlocked && (
                    <StyledErrorMessage>
                      Please retry login after 15 minutes or contact your
                      administrator
                    </StyledErrorMessage>
                  )}

                  {!isLoginBlocked && errorMessage && (
                    <StyledErrorMessage>
                      Username or Password is incorrect
                    </StyledErrorMessage>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </StyledForm>
        </Grid>
      </Grid>
    </Grid>
  );
};
export default LoginForm;
